import { z } from 'zod';

import { ImpressionDeviceValueSchema } from '../constants/impression-device.js';
import {
  AdvancedFiltersSchema,
  CompetitorFiltersSchema,
  DashboardMode,
} from '../events/workspace.js';

/** Overview page filter fields committed via the Apply button (excludes chart display state). */
export const OverviewPagePresetFiltersSchema = z.object({
  groupBy: z.string(),
  groupByValues: z.array(z.string()),
  subFeature: z.array(z.string()),
  featureTimeFrame: z.array(z.string()),
  objectType: z.array(z.string()),
  effectiveStatus: z.array(z.string()),
  myBrandEffectiveStatus: z.array(z.string()),
  audienceSegment: z.array(z.string()).default([]),
  device: z.array(ImpressionDeviceValueSchema).default([]),
  aspectRatio: z.array(z.string()).default([]),
  scenario: z.array(z.string()),
  advancedFilters: AdvancedFiltersSchema.optional().default([]),
  breakdown: z.string(),
  performanceMetric: z.string(),
});
export type OverviewPagePresetFilters = z.infer<typeof OverviewPagePresetFiltersSchema>;

/**
 * Stored picker fields may be omitted, or written as `null` by older clients.
 * `null` must become absent — `z.string().optional()` rejects it and listing
 * would then fail while parsing the stored JSON blob.
 */
const optionalPersistedString = z
  .string()
  .nullish()
  .transform((value) => value ?? undefined);

/**
 * Picker identity on a stored filter preset. Optional so snapshots from before
 * AD-2546 still parse. Catalog presets persist `activePresetId` and leave dates
 * empty so they re-resolve against today; custom ranges persist the pair.
 */
export const PresetDateSelectionSchema = z.object({
  activePresetId: optionalPersistedString,
  startDate: optionalPersistedString,
  endDate: optionalPersistedString,
});

/** My Brand global filters as stored in the dashboard Zustand store (null = All). */
export const MyBrandPresetGlobalFiltersSchema = z
  .object({
    platforms: z.array(z.string()).nullable(),
    accounts: z.array(z.string()).nullable(),
    products: z.array(z.string()).nullable(),
    dateRange: z.enum(['7d', '30d', '90d', '12m']),
  })
  .merge(PresetDateSelectionSchema);
export type MyBrandPresetGlobalFilters = z.infer<typeof MyBrandPresetGlobalFiltersSchema>;

/** Competitor global filters plus the picker identity the wire schema omits. */
export const CompetitorPresetGlobalFiltersSchema = CompetitorFiltersSchema.merge(
  PresetDateSelectionSchema,
);
export type CompetitorPresetGlobalFilters = z.infer<typeof CompetitorPresetGlobalFiltersSchema>;

export const FilterPresetPayloadSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('my-brand'),
    page: OverviewPagePresetFiltersSchema,
    global: MyBrandPresetGlobalFiltersSchema,
  }),
  z.object({
    mode: z.literal('competitors'),
    page: OverviewPagePresetFiltersSchema,
    global: CompetitorPresetGlobalFiltersSchema,
  }),
]);
export type FilterPresetPayload = z.infer<typeof FilterPresetPayloadSchema>;

export const CreateFilterPresetRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
  })
  .and(FilterPresetPayloadSchema);
export type CreateFilterPresetRequest = z.infer<typeof CreateFilterPresetRequestSchema>;

export const FilterPresetScopeSchema = z.enum(['personal', 'company']);
export type FilterPresetScope = z.infer<typeof FilterPresetScopeSchema>;

export const FilterPresetDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  mode: DashboardMode,
  filters: FilterPresetPayloadSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  scope: FilterPresetScopeSchema,
  canManage: z.boolean(),
  canDelete: z.boolean(),
  canReorder: z.boolean(),
});
export type FilterPresetDto = z.infer<typeof FilterPresetDtoSchema>;

export const ListFilterPresetsQuerySchema = z.object({
  mode: DashboardMode,
  includeCompany: z
    .string()
    .optional()
    .transform((value) => value === 'true' || value === '1'),
});
export type ListFilterPresetsQuery = z.infer<typeof ListFilterPresetsQuerySchema>;

export const ReorderFilterPresetsBodySchema = z.object({
  mode: DashboardMode,
  ids: z.array(z.string().min(1)).min(1),
});
export type ReorderFilterPresetsBody = z.infer<typeof ReorderFilterPresetsBodySchema>;
